import * as XLSX from 'xlsx';
import { formatDate } from './helpers';
import { getCurrent, getPrevious, getChildren, getCode, getName } from './TwoColumnReport';
import { sumGroup } from '../TrialBalance/TrialBalanceTable';
import { MONTHS, rowTotal, monthlyColumnTotals } from '../MonthlyIncome/MonthlyIncomeTable';
import { balanceLabel } from '../GroupLedger/AccountLedger';

// Excel downloads for the account reports. Each sheet carries the same figures
// as the screen, read through the screen's own accessors, always fully expanded.
// Headings use reportInfo rather than the filters: a date changed without
// clicking Get Report must not relabel the data already loaded.

const num = (v) => parseFloat(v) || 0;
const amountOrBlank = (v) => (num(v) > 0 ? num(v) : '');
const indent = (depth) => '    '.repeat(depth);

const periodLabel = (from, to) => `${formatDate(from)} to ${formatDate(to)}`;

const heading = (title, info, period) => [
    [info?.societyTitle ? `${info.societyTitle} - ${title}` : title],
    [period],
    [],
];

const download = (rows, cols, sheetName, fileName) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = cols.map((wch) => ({ wch }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${fileName}.xlsx`);
};

// --------------------------------------------------- I&E and Balance Sheet --
const treeRows = (nodes, depth = 0) => (nodes || []).flatMap((node) => {
    const code = depth === 0 && getCode(node) ? ` (${getCode(node)})` : '';
    return [
        [`${indent(depth)}${getName(node)}${code}`, num(getCurrent(node)), num(getPrevious(node))],
        ...treeRows(getChildren(node), depth + 1),
    ];
});

const sideRows = (side, columns) => [
    [side.title, ...columns],
    ...treeRows(side.nodes),
    ...(side.extraRow ? [[side.extraRow.label, num(getCurrent(side.extraRow)), num(getPrevious(side.extraRow))]] : []),
    [side.totalLabel, num(side.totalCurrent), num(side.totalPrevious)],
    ...(side.grandCurrent !== undefined ? [['Grand Total', num(side.grandCurrent), num(side.grandPrevious)]] : []),
    [],
];

export const twoColumnRows = ({ title, info, period, columns, sides }) => [
    ...heading(title, info, period),
    ...sides.flatMap((side) => sideRows(side, columns)),
];

export const exportIncomeExpenditure = (data, sides) => {
    const info = data.reportInfo;
    const rows = twoColumnRows({
        title: 'Income & Expenditure',
        info,
        period: periodLabel(info?.fromDate, info?.toDate),
        columns: ['Current Year', 'Previous Year'],
        sides,
    });
    download(rows, [44, 16, 16], 'Income & Expenditure', `Income_Expenditure_${info?.fromDate}_to_${info?.toDate}`);
};

export const exportBalanceSheet = (data, sides) => {
    const info = data.reportInfo;
    const rows = twoColumnRows({
        title: 'Balance Sheet',
        info,
        period: `As at ${formatDate(info?.toCurrentDate)}`,
        columns: ['Current Year', 'Previous Year'],
        sides,
    });
    download(rows, [44, 16, 16], 'Balance Sheet', `Balance_Sheet_${info?.toCurrentDate}`);
};

// ------------------------------------------------------------ Trial Balance --
const balanceCells = (b) => [
    num(b.opening_dr), num(b.opening_cr),
    num(b.during_year_dr), num(b.during_year_cr),
    num(b.closing_dr), num(b.closing_cr),
];

const flattenBalance = (a) => ({
    opening_dr: a?.opening?.debit, opening_cr: a?.opening?.credit,
    during_year_dr: a?.duringYear?.debit, during_year_cr: a?.duringYear?.credit,
    closing_dr: a?.closing?.debit, closing_cr: a?.closing?.credit,
});

export const trialBalanceRows = (data) => {
    const info = data.reportInfo;
    const groups = data.accounts || [];
    const totals = data.totals;
    const balanced = totals
        && Math.abs(num(totals.closing?.debit) - num(totals.closing?.credit)) < 0.01;
    return [
        ...heading('Trial Balance', info, periodLabel(info?.fromDate, info?.toDate)),
        ['', '', 'Opening Balance', '', 'During the Year', '', 'Closing Balance', ''],
        ['Account Name', 'Account Code', 'Dr', 'Cr', 'Dr', 'Cr', 'Dr', 'Cr'],
        ...groups.flatMap((group) => [
            [`${group.groupName} (${group.groupCode})`, '', ...balanceCells(sumGroup(group))],
            ...(group.accounts || []).map((acc) => [
                `${indent(1)}${acc.name}`, acc.accountCode, ...balanceCells(flattenBalance(acc)),
            ]),
        ]),
        ...(totals
            ? [[`Grand Total (${balanced ? 'Balanced' : 'Unbalanced'})`, '', ...balanceCells(flattenBalance(totals))]]
            : []),
    ];
};

export const exportTrialBalance = (data) => {
    download(
        trialBalanceRows(data),
        [44, 14, 14, 14, 14, 14, 14, 14],
        'Trial Balance',
        `Trial_Balance_${data.reportInfo?.toDate}`
    );
};

// ------------------------------------------- Monthly Income and Expenditure --
export const monthlyRows = (title, data) => {
    const info = data.reportInfo;
    const rows = data.accounts || data.rows || [];
    const totals = monthlyColumnTotals(rows, data.totals);
    return [
        ...heading(title, info, periodLabel(info?.fromDate, info?.toDate)),
        ['Account Name', ...MONTHS.map((m) => m.label), 'Total'],
        ...rows.map((row) => {
            const name = row.name || row.accountName || row.parentAccountName || '';
            const code = row.ac_code || row.code || row.accountCode || '';
            return [`${name}${code ? ` (${code})` : ''}`, ...MONTHS.map((m) => num(row[m.key])), num(rowTotal(row))];
        }),
        ['Total', ...MONTHS.map((m) => num(totals[m.key])), num(totals.total)],
    ];
};

export const exportMonthlyReport = (title, data) => {
    download(
        monthlyRows(title, data),
        [44, ...MONTHS.map(() => 12), 14],
        title,
        `${title.replace(/\s+/g, '_')}_${data.reportInfo?.financialYear}`
    );
};

// ------------------------------------------------------------- Group Ledger --
const ledgerRows = (ledger) => {
    const opening = ledger.openingEntries || [];
    const openingBalance = num(ledger.openingBalance);
    return [
        [`${ledger.accountName} (${ledger.accountCode})`],
        ['Date', 'Voucher No', 'Particulars', 'Dr Amount', 'Cr Amount', 'Balance'],
        ...(opening.length > 0
            ? opening.map((op) => [
                formatDate(op.date), '', op.particulars || 'Opening Balance',
                amountOrBlank(op.debit), amountOrBlank(op.credit), balanceLabel(op.balance),
            ])
            : [[
                '', '', 'Opening Balance',
                amountOrBlank(openingBalance), amountOrBlank(-openingBalance), balanceLabel(openingBalance),
            ]]),
        ...(ledger.entries || []).map((tx) => [
            formatDate(tx.date), tx.voucherNo || '', tx.particulars || '',
            amountOrBlank(tx.debit), amountOrBlank(tx.credit), balanceLabel(tx.balance),
        ]),
        ['Total', '', '', num(ledger.totalDebit), num(ledger.totalCredit), balanceLabel(ledger.totalBalance)],
        [],
    ];
};

export const groupLedgerRows = (data) => {
    const info = data.reportInfo;
    return [
        ...heading('Group Ledger', info, periodLabel(info?.fromDate, info?.toDate)),
        [`${info?.groupAccountName || ''} (${info?.groupAccountCode || ''})`],
        [],
        ...(data.ledgers || []).flatMap(ledgerRows),
    ];
};

export const exportGroupLedger = (data) => {
    const info = data.reportInfo;
    download(
        groupLedgerRows(data),
        [12, 18, 40, 14, 14, 18],
        'Group Ledger',
        `Group_Ledger_${info?.groupAccountCode}_${info?.fromDate}_to_${info?.toDate}`
    );
};
